/**
 * Client Profile Card
 * Displays client profile information summary
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { User, Edit, ChevronDown, Mail, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ClientProfileCard = ({ businessProfile, isExpanded = true, onToggle }) => {
  const navigate = useNavigate();

  // Helper to calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Helper to format full name with prefix and suffix
  const formatFullName = (prefix, firstName, lastName, suffix) => {
    const parts = [];
    if (prefix) parts.push(prefix);
    if (firstName) parts.push(firstName);
    if (lastName) parts.push(lastName);
    if (suffix) parts.push(suffix);
    return parts.join(' ');
  };

  // Helper to safely parse dependents_info
  const getDependentsArray = () => {
    if (!businessProfile?.dependents_info) return [];

    // If it's already an array, return it
    if (Array.isArray(businessProfile.dependents_info)) {
      return businessProfile.dependents_info;
    }

    // If it's a JSON string, parse it
    if (typeof businessProfile.dependents_info === 'string') {
      try {
        const parsed = JSON.parse(businessProfile.dependents_info);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error('Error parsing dependents_info:', e);
        return [];
      }
    }

    return [];
  };

  const clientAge = calculateAge(businessProfile?.client_date_of_birth);
  const spouseAge = calculateAge(businessProfile?.spouse_date_of_birth);
  const dependents = getDependentsArray();

  // Format client full name
  const clientFullName = formatFullName(
    businessProfile?.client_prefix,
    businessProfile?.client_first_name,
    businessProfile?.client_last_name,
    businessProfile?.client_suffix
  );

  // Format spouse full name (try new fields first, fallback to old spouse_name field)
  const spouseFullName = businessProfile?.spouse_first_name || businessProfile?.spouse_last_name
    ? formatFullName(
        businessProfile?.spouse_prefix,
        businessProfile?.spouse_first_name,
        businessProfile?.spouse_last_name,
        businessProfile?.spouse_suffix
      )
    : businessProfile?.spouse_name;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={onToggle}
            className="flex items-center space-x-2 flex-1 text-left hover:opacity-80 transition"
          >
            <ChevronDown
              className={`text-blue-600 transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
              size={18}
            />
            <User className="text-blue-600" size={20} />
            <h3 className="text-base font-bold text-gray-900">Client Profile</h3>
          </button>
          <button
            onClick={() => navigate('/business-profile?section=client')}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition min-h-[44px] min-w-[44px] flex items-center justify-center"
            title="Edit Client Profile"
          >
            <Edit size={16} />
          </button>
        </div>

      {isExpanded && (
        <div>
          {businessProfile ? (
            <div className="space-y-1 text-xs">
          {clientFullName && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">Name:</span>
              <span className="font-semibold text-right">
                {clientFullName}
              </span>
            </div>
          )}
          {clientAge && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">Age:</span>
              <span className="font-semibold text-right">{clientAge}</span>
            </div>
          )}
          {businessProfile.client_email && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">Email:</span>
              <a href={`mailto:${businessProfile.client_email}`} className="font-semibold text-blue-600 hover:underline text-right truncate">
                {businessProfile.client_email}
              </a>
            </div>
          )}
          {businessProfile.client_phone && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">Phone:</span>
              <a href={`tel:${businessProfile.client_phone}`} className="font-semibold text-blue-600 hover:underline text-right">
                {businessProfile.client_phone}
              </a>
            </div>
          )}
          {(businessProfile.client_city || businessProfile.client_state) && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">Location:</span>
              <span className="font-semibold text-right">
                {businessProfile.client_city && businessProfile.client_state
                  ? `${businessProfile.client_city}, ${businessProfile.client_state}`
                  : businessProfile.client_city || businessProfile.client_state}
              </span>
            </div>
          )}
          {spouseFullName && (
            <div className="flex justify-between items-start gap-2 border-t border-gray-200 pt-1 mt-1">
              <span className="text-gray-600 flex-shrink-0">Spouse:</span>
              <span className="font-semibold text-right">{spouseFullName}</span>
            </div>
          )}
          {spouseAge && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">Spouse Age:</span>
              <span className="font-semibold text-right">{spouseAge}</span>
            </div>
          )}
          {businessProfile.spouse_email && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">Email:</span>
              <a href={`mailto:${businessProfile.spouse_email}`} className="font-semibold text-blue-600 hover:underline text-right truncate">
                {businessProfile.spouse_email}
              </a>
            </div>
          )}
          {businessProfile.spouse_phone && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">Phone:</span>
              <a href={`tel:${businessProfile.spouse_phone}`} className="font-semibold text-blue-600 hover:underline text-right">
                {businessProfile.spouse_phone}
              </a>
            </div>
          )}
          {businessProfile.spouse_involved_in_business && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">In Business:</span>
              <span className="font-semibold text-green-600 text-right">Yes</span>
            </div>
          )}
          {businessProfile.num_dependents > 0 && (
            <div className="flex justify-between items-start gap-2 border-t border-gray-200 pt-1 mt-1">
              <span className="text-gray-600 flex-shrink-0">Dependents:</span>
              <span className="font-semibold text-right">{businessProfile.num_dependents}</span>
            </div>
          )}
          {dependents.length > 0 && (
            <div className="mt-0.5">
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 ml-1">
                {dependents.map((dependent, index) => (
                  <div key={index} className="flex items-center gap-1 text-xs py-0.5">
                    <span className="text-gray-700">{dependent.name || `Dependent ${index + 1}`}</span>
                    {dependent.age && (
                      <span className="text-gray-500 text-xs">({dependent.age})</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        ) : (
          <p className="text-xs text-gray-500">No client profile available</p>
        )}
      </div>
      )}
      </div>
    </div>
  );
};

ClientProfileCard.propTypes = {
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  businessProfile: PropTypes.shape({
    client_prefix: PropTypes.string,
    client_first_name: PropTypes.string,
    client_last_name: PropTypes.string,
    client_suffix: PropTypes.string,
    client_date_of_birth: PropTypes.string,
    client_email: PropTypes.string,
    client_phone: PropTypes.string,
    client_city: PropTypes.string,
    client_state: PropTypes.string,
    spouse_name: PropTypes.string,  // Keep for backwards compatibility
    spouse_prefix: PropTypes.string,
    spouse_first_name: PropTypes.string,
    spouse_last_name: PropTypes.string,
    spouse_suffix: PropTypes.string,
    spouse_date_of_birth: PropTypes.string,
    spouse_email: PropTypes.string,
    spouse_phone: PropTypes.string,
    spouse_involved_in_business: PropTypes.bool,
    num_dependents: PropTypes.number,
    dependents_info: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        age: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        relationship: PropTypes.string
      })
    )
  })
};

export default ClientProfileCard;
